import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
// import UpdateProductModal from "components/Modals/UpdateProductModal";
import { menuApiService } from "services/menuApiService";
import DeleteModal from "pages/Calendar/DeleteModal";
import menuImg from "../../assets/images/qwikBistroImg.webp";
import UpdateProductModal from "./UpdateProductModal";


const AllMenuItems = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Fetch products
  const fetchProducts = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await menuApiService.getAllProducts();
      setProducts(data);
    } catch (error) {
      toast.error("Failed to fetch products");
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const showDeleteModal = (id) => {
    setSelectedProductId(id);
    setDeleteModal(true);
  };

  const handleDeleteProduct = async () => {
    setDeletingProduct(true);
    try {
      await menuApiService.deleteProduct(selectedProductId);
      toast.success("Product deleted successfully");
      setDeleteModal(false);
      fetchProducts(false);
    } catch (error) {
      toast.error("Failed to delete product");
      console.error("Error deleting product:", error);
    } finally {
      setDeletingProduct(false);
    }
  };

  const handleUpdateProduct = (product) => {
    setSelectedProduct(product);
    setUpdateModalOpen(true);
  };

  const handleUpdateSuccess = () => {
    fetchProducts(false);
    toast.success("Product updated successfully");
  };

  // Get unique categories for filter
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(products.map(product => product.category))];
    return uniqueCategories.filter(category => category).sort();
  }, [products]);

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          (product.id && product.id.toString().toLowerCase().includes(query)) ||
          (product.item_name && product.item_name.toLowerCase().includes(query)) ||
          (product.description && product.description.toLowerCase().includes(query)) ||
          (product.category && product.category.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [products, categoryFilter, searchQuery]);

  const formatCurrency = (amount) => {
    if (!amount) return "$0";
    return `$${parseFloat(amount).toLocaleString('en-US')}`;
  };

  const getCategoryBadge = (category) => {
    const categoryMap = {
      'appetizer': { color: 'primary', text: 'Appetizers' },
      'main course': { color: 'success', text: 'Main Dish' },
      'fast food': { color: 'info', text: 'Sides / Extra' },
      'dessert': { color: 'warning', text: 'Dessert' },
    };

    const config = categoryMap[category] || { color: 'secondary', text: category || 'Uncategorized' };
    return <span className={`badge bg-${config.color}`}>{config.text}</span>;
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            #{row.original.id}
          </div>
        ),
      },
      {
        header: "Image",
        accessorKey: "image1",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-center">
            {row.original.image1 ? (
              <img
                src={row.original.image1}
                alt="Product"
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
              // style={{
              //   width: "60px",
              //   height: "60px",
              //   borderRadius: "50%",
              //   backgroundColor: "#f0f0f0",
              //   display: "flex",
              //   alignItems: "center",
              //   justifyContent: "center",
              //   margin: "0 auto",
              // }}
              >
                {/* <i className="fas fa-image text-muted"></i> */}
                <img
                  src={menuImg}
                  alt="Product"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              </div>
            )}
          </div>
        ),
      },
      {
        header: "Name",
        accessorKey: "item_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row.original.item_name || "-"}
          </div>
        ),
      },
      {
        header: "Description",
        accessorKey: "description",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div>
            <small className="d-block text-truncate" style={{ maxWidth: "300px" }}>
              {row.original.description || "-"}
            </small>
          </div>
        ),
      },
      {
        header: "Category",
        accessorKey: "category",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          // getCategoryBadge(row.original.category)
          <div>
            {row.original.category}
          </div>
        ),
      },
      {
        header: "Price",
        accessorKey: "price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className=" text-success">
            {formatCurrency(row.original.price)}
          </div>
        ),
      },
      {
        header: "Discount",
        accessorKey: "discount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {row.original.discount ? `${row.original.discount}%` : "-"}
          </div>
        ),
      },
      // {
      //   header: "Quantity",
      //   accessorKey: "quantity",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => (
      //     <div className="">
      //       {row.original.quantity || "0"}
      //     </div>
      //   ),
      // },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <button
              onClick={() => handleUpdateProduct(row.original)}
              className="btn btn-warning btn-sm"
              title="Edit Product"
            >
              <i className="fas fa-edit"></i>
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={() => showDeleteModal(row.original.id)}
              title="Delete Product"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Calculate statistics
  const totalProducts = products.length;
  const totalValue = products.reduce((sum, product) => sum + parseFloat(product.price || 0), 0);
  const appetizerCount = products.filter(product => product.category === 'appetizer').length;
  const mainCourseCount = products.filter(product => product.category === 'main course').length;
  const fastFoodCount = products.filter(product => product.category === 'fast food').length;

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Products" breadcrumbItem="All Products" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Products</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{totalProducts}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-primary rounded-circle">
                        <i className="fas fa-utensils font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Total Value</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{formatCurrency(totalValue)}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-success rounded-circle">
                        <i className="fas fa-dollar-sign font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Main Dishes</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{mainCourseCount}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-info rounded-circle">
                        <i className="fas fa-hamburger font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card card-h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h5 className="text-muted fw-medium mb-3">Sides / Extras</h5>
                    <h2 className="mb-0">
                      <span className="counter-value">{fastFoodCount}</span>
                    </h2>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-sm">
                      <span className="avatar-title bg-warning rounded-circle">
                        <i className="fas fa-french-fries font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Filters and Actions */}
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3 align-items-center">

                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by name, description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ width: "300px" }}
                  />
                </div>


                <div className="d-flex align-items-center gap-2">
                  {/* <label className="form-label mb-0">Category:</label> */}
                  <select
                    className="form-select"
                    style={{ width: "auto" }}
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {categories.map((category, index) => (
                      <option key={index} value={category}>
                        {category === 'appetizer' ? 'Appetizers' :
                          category === 'main course' ? 'Main Dish' :
                            category === 'fast food' ? 'Sides / Extra' :
                              category === 'dessert' ? 'Dessert' : category}
                      </option>
                    ))}
                  </select>
                </div>



                {(categoryFilter || searchQuery) && (
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setCategoryFilter("");
                      setSearchQuery("");
                    }}
                  >
                    <i className="fas fa-times me-1"></i> Clear Filters
                  </button>
                )}
              </div>

              <div className="d-flex gap-2">
                <button
                  onClick={() => navigate("/create-menu-item")}
                  className="btn btn-success"
                >
                  <i className="fas fa-plus me-1"></i> Create Menu Item
                </button>

                <button
                  className="btn btn-light"
                  onClick={() => fetchProducts(false)}
                  disabled={refreshing}
                  title="Refresh Products"
                >
                  <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* <div className="mb-3">
                  <h5 className="card-title mb-0">
                    Showing {filteredProducts.length} of {totalProducts} products
                  </h5>
                </div> */}

                <TableContainer
                  columns={columns}
                  data={filteredProducts}
                  isGlobalFilter={false}
                  isPagination={true}
                  pagination="pagination"
                  paginationWrapper="dataTables_paginate paging_simple_numbers"
                  tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                  isCustomPageSize={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteProduct}
          loader={deletingProduct}
          onCloseClick={() => setDeleteModal(false)}
          title="Delete Product"
          message="Are you sure you want to delete this product? This action cannot be undone."
        />

        {selectedProduct && (
          <UpdateProductModal
            isOpen={updateModalOpen}
            toggle={() => setUpdateModalOpen(false)}
            product={selectedProduct}
            onUpdateSuccess={handleUpdateSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default AllMenuItems;